/**
 * Link detection helpers — used by DMs, comments, and update displays to
 * turn bare URLs in user-entered text into clickable links and fetch
 * rich previews for them.
 *
 * Design notes:
 *   - The URL regex is intentionally conservative. False-positives in a
 *     team chat (e.g., "some.words.together" tripping the regex and
 *     rendering as a link) are more annoying than false-negatives.
 *   - We only preview the FIRST URL in a message. Slack does the same.
 *     Multi-link messages would otherwise dominate the chat.
 */

// Match http(s) links. Requires a valid scheme to avoid classifying
// "acme.v2" or "1.2.3" as URLs. Trailing punctuation (.,!?;:) is
// excluded from the match so "check this https://foo.com." doesn't
// capture the period.
const URL_REGEX = /(https?:\/\/[^\s<>"']+[^\s<>"'.,!?;:)\]])/g

/**
 * Split text into an array of { type: 'text' | 'url', value } segments.
 * Stable across identical inputs (safe to use as React keys).
 */
export function parseSegments(text) {
  if (!text) return []
  const segments = []
  let last = 0
  const matches = [...text.matchAll(URL_REGEX)]
  for (const m of matches) {
    const start = m.index
    if (start > last) {
      segments.push({ type: 'text', value: text.slice(last, start) })
    }
    segments.push({ type: 'url', value: m[0] })
    last = start + m[0].length
  }
  if (last < text.length) {
    segments.push({ type: 'text', value: text.slice(last) })
  }
  return segments
}

/**
 * Return the first http(s) URL in `text`, or null.
 */
export function firstUrl(text) {
  if (!text) return null
  URL_REGEX.lastIndex = 0
  const m = URL_REGEX.exec(text)
  return m ? m[0] : null
}

/**
 * Best-effort hostname extraction without throwing on malformed URLs.
 */
export function hostnameOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}
